// STEP STUDIO-OVERLAY-DRAG-RESIZE-1 · 정규화 rect 로 배치되는 Overlay 상자 (Studio · 소비자 · Egress 공용).
//   · 무대(부모) 크기를 자체 측정 → 비율 변경 시 위치·크기 비례 유지.
//   · 콘텐츠는 base 폭으로 렌더 후 transform scale (글자 잘림 없이 확대·축소).
//   · editing=true 일 때만 드래그/모서리 리사이즈 (마우스·터치 공통 Pointer Events).
import React, { useEffect, useRef, useState } from 'react';

const useStageSize = (ref) => {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const upd = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    upd();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', upd);
      return () => window.removeEventListener('resize', upd);
    }
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
};

export const OverlayFreeBox = ({
  rect, base, editing = false, label = '', clamp = (r) => r, fluid = false, resizable = true,
  onChange, onCommit, testid = 'overlay-free-box', zIndex = 10, children,
}) => {
  const stageRef = useRef(null);
  const { w: W, h: H } = useStageSize(stageRef);
  const dragRef = useRef(null);
  // fluid: 실제 폭(rect.w)으로 렌더 → 내부 반응형(cqw) · 기본: base 폭 렌더 후 scale.
  const s = fluid ? 1 : (rect && base ? rect.w / base : 1);
  const childW = Math.max(1, (fluid ? (rect?.w || base) : base) * W);

  const onDown = (mode) => (e) => {
    if (!editing) return;
    e.preventDefault(); e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    dragRef.current = { mode, x: e.clientX, y: e.clientY, start: rect, last: rect };
  };
  const onMove = (e) => {
    const d = dragRef.current;
    if (!d || !W || !H) return;
    e.preventDefault(); e.stopPropagation();
    const dx = (e.clientX - d.x) / W;
    const dy = (e.clientY - d.y) / H;
    const next = clamp(d.mode === 'move'
      ? { ...d.start, cx: d.start.cx + dx, cy: d.start.cy + dy }
      : { ...d.start, w: d.start.w + 2 * dx });
    d.last = next;
    onChange?.(next);
  };
  const onUp = (e) => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    e.stopPropagation();
    if (d.last !== d.start) onCommit?.(d.last);
  };

  if (!rect) return null;
  return (
    <div ref={stageRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex }}>
      {W > 0 && (
        <div style={{
               position: 'absolute', left: rect.cx * W, top: rect.cy * H, width: childW,
               transform: `translate(-50%, -50%) scale(${s})`, transformOrigin: 'center',
               pointerEvents: editing ? 'auto' : 'none', touchAction: editing ? 'none' : 'auto',
               cursor: editing ? 'move' : 'default',
               outline: editing ? `${2 / s}px dashed #FF3B30` : 'none',
               outlineOffset: editing ? `${3 / s}px` : 0,
             }}
             onPointerDown={onDown('move')} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
             data-testid={testid} data-cx={rect.cx} data-cy={rect.cy} data-w={rect.w} data-editing={editing ? '1' : '0'}>
          {typeof children === 'function' ? children(childW) : children}
          {editing && (
            <>
              <span style={{
                      position: 'absolute', left: 0, top: `${-22 / s}px`, fontSize: `${10 / s}px`,
                      padding: `${2 / s}px ${6 / s}px`, borderRadius: `${6 / s}px`,
                      background: '#FF3B30', color: '#fff', fontWeight: 900, whiteSpace: 'nowrap',
                    }}>{label}</span>
              {resizable && <span onPointerDown={onDown('resize')} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
                    style={{
                      position: 'absolute', right: `${-14 / s}px`, bottom: `${-14 / s}px`,
                      width: `${28 / s}px`, height: `${28 / s}px`, borderRadius: '50%',
                      background: '#fff', border: `${3 / s}px solid #FF3B30`, cursor: 'nwse-resize', touchAction: 'none',
                    }}
                    data-testid={`${testid}-resize`} />}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OverlayFreeBox;
